import { Node } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";

import { cn } from "@/lib/utils";
import { LayoutBlockComponent } from "./LayoutBlockNodeView";

export type LayoutVariant = "twoColumns" | "fullWidth" | "card";

export const LayoutBlock = Node.create({
  name: "layoutBlock",

  group: "block",
  content: "block+",
  defining: true,

  addAttributes() {
    return {
      variant: {
        default: "twoColumns" as LayoutVariant,
      },
      cardAlign: {
        default: "full" as "full" | "center",
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div[data-layout-block]",
        getAttrs: (dom) => {
          const el = dom as HTMLElement;
          const raw = el.getAttribute("data-layout-variant");
          const variant: LayoutVariant =
            raw === "fullWidth" || raw === "card" || raw === "twoColumns"
              ? (raw as LayoutVariant)
              : "twoColumns";

          const alignRaw = el.getAttribute("data-layout-card-align");
          const cardAlign = alignRaw === "center" ? "center" : "full";

          return { variant, cardAlign };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const variant: LayoutVariant =
      (node.attrs.variant as LayoutVariant) ?? "twoColumns";
    const cardAlign: "full" | "center" =
      node.attrs.cardAlign === "center" ? "center" : "full";

    let className = "my-4 rounded-xl border shadow-sm text-sm leading-relaxed";

    if (variant === "twoColumns") {
      className = cn(
        className,
        "border-slate-200 bg-slate-50/80 px-4 py-3 md:px-5 md:py-4 dark:border-slate-700 dark:bg-slate-900/70 grid gap-4 md:grid-cols-2",
      );
    } else if (variant === "fullWidth") {
      className = cn(
        className,
        "my-6 border-amber-200 bg-amber-50/85 px-4 py-4 text-[0.95rem] leading-relaxed shadow-sm dark:border-amber-500/50 dark:bg-amber-900/40",
      );
    } else if (variant === "card") {
      className = cn(
        className,
        "border-slate-200 bg-white/90 px-4 py-4 dark:border-slate-700 dark:bg-slate-900/80 overflow-hidden",
        cardAlign === "center" && "max-w-3xl mx-auto",
      );
    }

    const attrs: Record<string, string> = {
      "data-layout-block": "true",
      "data-layout-variant": variant,
      class: className,
    };

    if (variant === "card") {
      attrs["data-layout-card-align"] = cardAlign;
    }

    return ["div", attrs, 0];
  },

  addCommands() {
    return {
      insertLayoutBlock:
        (variant: LayoutVariant) =>
        ({ chain }) => {
          const content = [{ type: "paragraph" }];

          return chain()
            .focus()
            .insertContent({
              type: this.name,
              attrs: { variant },
              content,
            })
            .run();
        },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(LayoutBlockComponent);
  },
});

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    layoutBlock: {
      insertLayoutBlock: (variant: LayoutVariant) => ReturnType;
    };
  }
}
